import { z } from 'zod';
import { parse, isValid } from 'date-fns';

export const fileSchema = z
  .object({
    file: z
      .instanceof(FileList)
      .nullable()
      .refine(fileList => fileList && fileList.length > 0, {
        message: 'Arquivo é obrigatório',
      }),
  })
  .superRefine((data, ctx) => {
    const fileList = data.file;

    if (fileList) {
      const file = fileList[0];

      if (file.size > 5000000) {
        ctx.addIssue({
          path: ['file'],
          message: 'Tamanho máximo de 5MB',
          code: z.ZodIssueCode.custom,
        });
      }

      if (!['image/jpeg', 'application/pdf'].includes(file.type)) {
        ctx.addIssue({
          path: ['file'],
          message: 'Somente arquivos JPEG ou PDF são permitidos',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

const address = z.object({
  cep: z.optional(
    z
      .string()
      .min(9, { message: 'Campo obrigatório' })
      .regex(/^[0-9]{5}-[0-9]{3}$/, { message: 'CEP inválido' }),
  ),
  tipoLogradouro: z.optional(
    z.string().min(1, { message: 'Campo obrigatório' }),
  ),
  bairro: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  municipio: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  ufId: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  neighborhood: z.optional(z.string().nonempty('Campo obrigatório')),
  uf: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  ufDescription: z.optional(
    z.string().min(1, { message: 'Campo obrigatório' }),
  ),
  city: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  street: z.optional(z.string().min(1, { message: 'Campo obrigatório' })),
  number: z.string().min(1, { message: 'Campo obrigatório' }),
  complement: z.optional(z.string()),
});

export const dependenteSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  cpf: z.string().optional(),
  dtNascimento: z
    .string()
    .min(1, { message: 'Campo obrigatório' })
    .regex(/^(\d{2})\/(\d{2})\/(\d{4})$/, {
      message: 'Formato inválido (use DD/MM/AAAA)',
    })
    .refine(
      value => {
        const [dia, mes, ano] = value.split('/').map(Number);
        const date = new Date(ano, mes - 1, dia);

        // Verifica se o Date é válido e se os valores batem
        return (
          date.getFullYear() === ano &&
          date.getMonth() === mes - 1 &&
          date.getDate() === dia
        );
      },
      { message: 'Data de nascimento inválida' },
    ),
  parentesco: z.string().min(1, 'Parentesco é obrigatório'),
  cns: z.string().optional(),
  sexo: z.string().min(1, 'Sexo é obrigatório'),
  estCivil: z.string().min(1, 'Estado civil é obrigatório'),
  motherName: z.string().min(1, 'Nome da mãe é obrigatório'),
  vinculo: fileSchema.optional(),
});

export const funcionarioSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  cpf: z.string().min(14, 'CPF deve conter 14 caracteres'),
  dtNascimento: z
    .string()
    .min(1, { message: 'Campo obrigatório' })
    .regex(/^(\d{2})\/(\d{2})\/(\d{4})$/, {
      message: 'Formato inválido (use DD/MM/AAAA)',
    })
    .refine(
      value => {
        const [dia, mes, ano] = value.split('/').map(Number);
        const date = new Date(ano, mes - 1, dia);

        // Verifica se o Date é válido e se os valores batem
        return (
          date.getFullYear() === ano &&
          date.getMonth() === mes - 1 &&
          date.getDate() === dia
        );
      },
      { message: 'Data de nascimento inválida' },
    ),
  matricula: z.string().optional(),
  rg: z.string().min(1, 'RG é obrigatório'),
  orgao: z.string().min(1, 'Orgão é obrigatório'),
  orgaoUF: z.string().min(1, 'UF do orgão é obrigatório'),
  cns: z.string().optional(),
  sexo: z.string().min(1, 'Sexo é obrigatório'),
  estCivil: z.string().min(1, 'Estado civil é obrigatório'),
  telefoneRes: z.string().optional(),
  Celular: z.string().optional(),
  email: z.string().email('Email inválido'),
  motherName: z.string().min(1, 'Nome da mãe é obrigatório'),
  address: address.optional(),
  vinculo: fileSchema.optional(),
});

export const dependenteS4eSchema = z.object({
  codigoDependente: z.number(),
  nomeDependente: z.string(),
  numeroCpfDependente: z.string().optional(),
  dataNascimento: z.string(),
  codigoPlano: z.number(),
  nomePlano: z.string(),
  codigoANS: z.number(),
  nomeANS: z.string(),
  nomeMaeDependente: z.string(),
  valorPlano: z.string(),
  mesAnoPrimeiroPagamento: z.string(),
  numeroCarteira: z.string(),
  codigoSituacao: z.number(),
  nomeSituacao: z.string(),
  motivoCancelamentoHistorico: z.number(),
  motivoCancelamentoHistoricoNome: z.string().nullable(),
  dataSituacao: z.string(),
  dataAssinaturaContrato: z.string(),
  numeroCns: z.string().nullable(),
  codigoGrauParentesco: z.number(),
  grauParentescoNome: z.string(),
  sexo: z.string(),
  cdBeneficiarioAns: z.string().nullable(),
  cco: z.string().nullable(),
  codigoClinica: z.number(),
  nomeClinica: z.string().nullable(),
  codigoDentista: z.number(),
  nomeDentista: z.string().nullable(),
  carenciaAtendimento: z.number(),
  regraCarenciaPrimeiroAtendimento: z.string(),
  nomeSocial: z.string().nullable(),
  aliasInterno: z.string().nullable(),
  dataReajuste: z.string().nullable(),
  codigoVendedor: z.number(),
  nomeVendedor: z.string(),
  codigoAdesionista: z.number().nullable(),
  nomeAdesionista: z.string().nullable(),
  abrangencia: z.string(),
});

export const cadastradoSchema = z.object({
  codigo: z.number(),
  cpf: z.string(),
  nome: z.string(),
  matricula: z.string().nullable(),
  dependentes: z.array(dependenteS4eSchema),
});

export interface Dependente {
  nome: string;
  cpf: string;
  dtNascimento: string;
  rg: string;
  orgao: string;
  orgaoUF: string;
  parentesco: string;
  cns: string;
  sexo: string;
  estCivil: string;
  motherName: string;
  email: string;
}

interface Address {
  cep: string;
  neighborhood: string;
  uf: string;
  city: string;
  street: string;
  number: string;
  complement: string;
}

export interface Funcionario {
  nome: string;
  cpf: string;
  dtNascimento: string;
  matricula: string;
  rg: string;
  orgao: string;
  orgaoUF: string;
  UF: string;
  cns: string;
  sexo: string;
  estCivil: string;
  telefoneRes?: string | null;
  Celular: string;
  email: string;
  motherName: string;
  dependentes?: Dependente[];
  address?: Address;
}

export const schema = z.object({
  funcionario: funcionarioSchema,
});

export type FuncionarioSchema = z.infer<typeof schema>;
